/**
 * Central mock registry
 * Routes API calls to appropriate mock handlers based on URL and method
 *
 * IMPORTANT: Mocks are ONLY active when REACT_APP_USE_MOCKS=true
 * NO automatic fallback when backend fails
 */

import { isMockEnabled, isModuleMockEnabled } from './config';
import tasksHandlers from './handlers/tasks';
import contactsHandlers from './handlers/contacts';
import usersHandlers from './handlers/users';
import agendaHandlers from './handlers/agenda';

/**
 * Route mock request based on URL and method
 *
 * @param {string} method - HTTP method (GET, POST, PUT, PATCH, DELETE)
 * @param {string} url - Request URL
 * @param {Object} data - Request body data
 * @param {Object} params - Query parameters
 * @returns {Promise<Object|null>} Mock response or null if no mock found
 */
export const routeMockRequest = async (method, url, data, params) => {
  // If mocks disabled globally, return null (use real API)
  if (!isMockEnabled()) {
    return null;
  }

  // Normalize URL (remove leading slash)
  const normalizedUrl = url.replace(/^\//, '');

  // ============================================================
  // TASKS MODULE
  // ============================================================
  if (isModuleMockEnabled('tasks')) {
    // ---- PROJECTS (BE 6.1) ----

    // GET /api/tasks/projects/ — list (archived, search)
    if (method === 'GET' && normalizedUrl === 'api/tasks/projects/') {
      return tasksHandlers.mockGetTaskProjects(params);
    }

    // GET /api/tasks/projects/{id}/ — detail with columns
    const getProjectMatch = normalizedUrl.match(/^api\/tasks\/projects\/(\d+)\/$/);
    if (method === 'GET' && getProjectMatch) {
      return tasksHandlers.mockGetTaskProject(getProjectMatch[1]);
    }

    // POST /api/tasks/projects/ — create
    if (method === 'POST' && normalizedUrl === 'api/tasks/projects/') {
      return tasksHandlers.mockCreateTaskProject(data);
    }

    // PATCH /api/tasks/projects/{id}/ — update metadata
    const patchProjectMatch = normalizedUrl.match(/^api\/tasks\/projects\/(\d+)\/$/);
    if (method === 'PATCH' && patchProjectMatch) {
      return tasksHandlers.mockUpdateTaskProject(patchProjectMatch[1], data);
    }

    // POST /api/tasks/projects/{id}/archive/
    const archiveProjectMatch = normalizedUrl.match(/^api\/tasks\/projects\/(\d+)\/archive\/$/);
    if (method === 'POST' && archiveProjectMatch) {
      return tasksHandlers.mockArchiveTaskProject(archiveProjectMatch[1]);
    }

    // POST /api/tasks/projects/{id}/restore/
    const restoreProjectMatch = normalizedUrl.match(/^api\/tasks\/projects\/(\d+)\/restore\/$/);
    if (method === 'POST' && restoreProjectMatch) {
      return tasksHandlers.mockRestoreTaskProject(restoreProjectMatch[1]);
    }

    // POST /api/tasks/projects/{id}/reorder-columns/
    const reorderColumnsMatch = normalizedUrl.match(/^api\/tasks\/projects\/(\d+)\/reorder-columns\/$/);
    if (method === 'POST' && reorderColumnsMatch) {
      return tasksHandlers.mockReorderTaskColumns(reorderColumnsMatch[1], data);
    }

    // ---- TASKS (BE 6.3) ----

    // GET /api/tasks/tasks/ - List with filters (paginated)
    if (method === 'GET' && normalizedUrl === 'api/tasks/tasks/') {
      return tasksHandlers.mockGetTasks(params);
    }

    // GET /api/tasks/tasks/summary/ - Scope summary
    if (method === 'GET' && normalizedUrl === 'api/tasks/tasks/summary/') {
      return tasksHandlers.mockGetTaskSummary(params);
    }

    // GET /api/tasks/tasks/{id}/ - Get single task
    const getTaskByIdMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/$/);
    if (method === 'GET' && getTaskByIdMatch) {
      return tasksHandlers.mockGetTaskById(getTaskByIdMatch[1]);
    }

    // POST /api/tasks/tasks/ - Create new task
    if (method === 'POST' && normalizedUrl === 'api/tasks/tasks/') {
      return tasksHandlers.mockCreateTask(data);
    }

    // POST /api/tasks/tasks/{id}/move/ - Atomic move
    const moveTaskMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/move\/$/);
    if (method === 'POST' && moveTaskMatch) {
      return tasksHandlers.mockMoveTask(moveTaskMatch[1], data);
    }

    // PATCH /api/tasks/tasks/{id}/toggle-completed/
    const toggleCompletedMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/toggle-completed\/$/);
    if (method === 'PATCH' && toggleCompletedMatch) {
      return tasksHandlers.mockToggleTaskCompleted(toggleCompletedMatch[1]);
    }

    // PUT /api/tasks/tasks/{id}/ - Update task
    const putTaskMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/$/);
    if (method === 'PUT' && putTaskMatch) {
      return tasksHandlers.mockUpdateTask(putTaskMatch[1], data);
    }

    // PATCH /api/tasks/tasks/{id}/ - Partial update task
    const patchTaskMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/$/);
    if (method === 'PATCH' && patchTaskMatch) {
      return tasksHandlers.mockUpdateTask(patchTaskMatch[1], data);
    }

    // DELETE /api/tasks/tasks/{id}/ - Delete task
    const deleteTaskMatch = normalizedUrl.match(/^api\/tasks\/tasks\/(\d+)\/$/);
    if (method === 'DELETE' && deleteTaskMatch) {
      return tasksHandlers.mockDeleteTask(deleteTaskMatch[1]);
    }

    // ---- TASK STATUSES (BE 6.2) ----

    // GET /api/tasks/tasks/available_statuses/ - Legacy alias
    if (method === 'GET' && normalizedUrl === 'api/tasks/tasks/available_statuses/') {
      return tasksHandlers.mockGetAvailableStatuses();
    }

    // GET /api/tasks/task-statuses/ - List (project-scoped)
    if (method === 'GET' && normalizedUrl === 'api/tasks/task-statuses/') {
      return tasksHandlers.mockGetTaskStatuses(params);
    }

    // POST /api/tasks/task-statuses/ - Create task status
    if (method === 'POST' && normalizedUrl === 'api/tasks/task-statuses/') {
      return tasksHandlers.mockCreateTaskStatus(data);
    }

    // POST /api/tasks/task-statuses/{id}/move-and-delete/
    const moveAndDeleteStatusMatch = normalizedUrl.match(/^api\/tasks\/task-statuses\/(\d+)\/move-and-delete\/$/);
    if (method === 'POST' && moveAndDeleteStatusMatch) {
      return tasksHandlers.mockMoveAndDeleteTaskStatus(moveAndDeleteStatusMatch[1], data);
    }

    // PATCH /api/tasks/task-statuses/{id}/ - Update task status
    const patchStatusMatch = normalizedUrl.match(/^api\/tasks\/task-statuses\/(\d+)\/$/);
    if (method === 'PATCH' && patchStatusMatch) {
      return tasksHandlers.mockUpdateTaskStatus(patchStatusMatch[1], data);
    }

    // DELETE /api/tasks/task-statuses/{id}/ - Delete task status (force is ignored)
    const deleteStatusMatch = normalizedUrl.match(/^api\/tasks\/task-statuses\/(\d+)\/$/);
    if (method === 'DELETE' && deleteStatusMatch) {
      return tasksHandlers.mockDeleteTaskStatus(deleteStatusMatch[1]);
    }

    // GET /api/tasks/task-statuses/{id}/in-use/ - Check if status is in use
    const checkStatusMatch = normalizedUrl.match(/^api\/tasks\/task-statuses\/(\d+)\/in-use\/$/);
    if (method === 'GET' && checkStatusMatch) {
      return tasksHandlers.mockCheckTaskStatusInUse(checkStatusMatch[1]);
    }

    // GET /api/tasks/taskhistory/ - Get all task history
    if (method === 'GET' && normalizedUrl === 'api/tasks/taskhistory/') {
      return tasksHandlers.mockGetTasksHistory(params);
    }

    // GET /api/tasks/task-history/ - Get task history for specific task
    if (method === 'GET' && normalizedUrl === 'api/tasks/task-history/') {
      const taskId = params?.task_id;
      if (taskId) {
        return tasksHandlers.mockGetTaskHistory(taskId);
      }
    }
  }

  // ============================================================
  // USERS MODULE
  // ============================================================
  if (isModuleMockEnabled('users')) {
    // GET /api/accounts/users/me/ - Get current user
    if (method === 'GET' && normalizedUrl === 'api/accounts/users/me/') {
      return usersHandlers.mockGetCurrentUser();
    }

    // GET /api/accounts/users/assignable/ - Get assignable users
    if (method === 'GET' && normalizedUrl === 'api/accounts/users/assignable/') {
      return usersHandlers.mockGetAssignableUsers();
    }
  }

  // ============================================================
  // CONTACTS MODULE
  // ============================================================
  if (isModuleMockEnabled('contacts')) {
    // GET /api/contacts/contacts/ - List all contacts
    if (method === 'GET' && normalizedUrl === 'api/contacts/contacts/') {
      return contactsHandlers.mockGetContacts(params);
    }

    // GET /api/contacts/contacts/{id}/ - Get single contact
    const getContactMatch = normalizedUrl.match(/^api\/contacts\/contacts\/(\d+)\/$/);
    if (method === 'GET' && getContactMatch) {
      return contactsHandlers.mockGetContact(getContactMatch[1]);
    }

    // POST /api/contacts/contacts/ - Create new contact
    if (method === 'POST' && normalizedUrl === 'api/contacts/contacts/') {
      return contactsHandlers.mockCreateContact(data);
    }

    // PUT /api/contacts/contacts/{id}/ - Update contact
    const putContactMatch = normalizedUrl.match(/^api\/contacts\/contacts\/(\d+)\/$/);
    if (method === 'PUT' && putContactMatch) {
      return contactsHandlers.mockUpdateContact(putContactMatch[1], data);
    }

    // PATCH /api/contacts/contacts/{id}/ - Partial update contact
    const patchContactMatch = normalizedUrl.match(/^api\/contacts\/contacts\/(\d+)\/$/);
    if (method === 'PATCH' && patchContactMatch) {
      return contactsHandlers.mockPatchContact(patchContactMatch[1], data);
    }

    // DELETE /api/contacts/contacts/{id}/ - Delete contact
    const deleteContactMatch = normalizedUrl.match(/^api\/contacts\/contacts\/(\d+)\/$/);
    if (method === 'DELETE' && deleteContactMatch) {
      return contactsHandlers.mockDeleteContact(deleteContactMatch[1]);
    }
  }

  // ============================================================
  // AGENDA MODULE
  // ============================================================
  if (isModuleMockEnabled('agenda')) {
    // GET /api/agenda/events/ - List all events
    if (method === 'GET' && normalizedUrl === 'api/agenda/events/') {
      return agendaHandlers.mockGetEvents(params);
    }

    // GET /api/agenda/events/{id}/ - Get single event
    const getEventMatch = normalizedUrl.match(/^api\/agenda\/events\/(\d+)\/$/);
    if (method === 'GET' && getEventMatch) {
      return agendaHandlers.mockGetEvent(getEventMatch[1]);
    }

    // POST /api/agenda/events/ - Create new event
    if (method === 'POST' && normalizedUrl === 'api/agenda/events/') {
      return agendaHandlers.mockCreateEvent(data);
    }

    // PUT /api/agenda/events/{id}/ - Update event
    const putEventMatch = normalizedUrl.match(/^api\/agenda\/events\/(\d+)\/$/);
    if (method === 'PUT' && putEventMatch) {
      return agendaHandlers.mockUpdateEvent(putEventMatch[1], data);
    }

    // PATCH /api/agenda/events/{id}/ - Partial update event
    const patchEventMatch = normalizedUrl.match(/^api\/agenda\/events\/(\d+)\/$/);
    if (method === 'PATCH' && patchEventMatch) {
      return agendaHandlers.mockPatchEvent(patchEventMatch[1], data);
    }

    // DELETE /api/agenda/events/{id}/ - Delete event
    const deleteEventMatch = normalizedUrl.match(/^api\/agenda\/events\/(\d+)\/$/);
    if (method === 'DELETE' && deleteEventMatch) {
      return agendaHandlers.mockDeleteEvent(deleteEventMatch[1]);
    }
  }

  // ============================================================
  // APPOINTMENTS MODULE (placeholder for future implementation)
  // ============================================================
  // if (isModuleMockEnabled('appointments')) {
  //   // Add appointments routes here
  // }

  // ============================================================
  // EMAILS MODULE (placeholder for future implementation)
  // ============================================================
  // if (isModuleMockEnabled('emails')) {
  //   // Add emails routes here
  // }

  // ============================================================
  // NOTIFICATIONS MODULE (placeholder for future implementation)
  // ============================================================
  // if (isModuleMockEnabled('notifications')) {
  //   // Add notifications routes here
  // }

  // ============================================================
  // AI AGENT MODULE (placeholder for future implementation)
  // ============================================================
  // if (isModuleMockEnabled('ai_agent')) {
  //   // Add AI agent routes here
  // }

  // No mock handler found, return null to use real API
  return null;
};

/**
 * Re-export config functions for convenience
 */
export { isMockEnabled, isModuleMockEnabled } from './config';
